import { z } from "zod";

const createPurchaserSchema = z.object({
  purchaserName: z
    .string()
    .trim()
    .min(1, "Purchaser name is required")
    .max(100, "Purchaser name must not exceed 100 characters"),
  purchaserKey: z.string().trim().min(1, "Purchaser key is required"),
  isActive: z.boolean(),
});

const updatePurchaserSchema = z.object({
  purchaserId: z.number().int().positive(),
  purchaserName: z
    .string()
    .trim()
    .min(1, "Purchaser name is required")
    .max(100, "Purchaser name must not exceed 100 characters"),
  isActive: z.boolean(),
});

const createBrandPurchaserAssociationSchema = z.object({
  purchaserId: z.number().int().positive(),
  brandId: z.number().int().positive(),
  isActive: z.boolean(),
});

const createPurchaserPortalKeySchema = z.object({
  purchaserId: z.number().int().positive(),
  purchaserKey: z
    .string()
    .trim()
    .min(1, "Purchaser key is required")
    .max(50, "Purchaser key must not exceed 50 characters"),
  portalKeyName: z
    .string()
    .trim()
    .min(1, "Portal key name is required")
    .max(255, "Portal key name must not exceed 255 characters"),
  portalKey: z
    .string()
    .trim()
    .min(1, "Portal key is required")
    .max(100, "Portal key must not exceed 100 characters")
    .regex(
      /^[A-Z]+(_[A-Z]+)*$/,
      "Only uppercase letters and underscores are allowed",
    ),
});

const updatePurchaserPortalKeySchema = z.object({
  id: z.number().int().positive(),
  purchaserId: z.number().int().positive(),
  purchaserKey: z
    .string()
    .trim()
    .min(1, "Purchaser key is required")
    .max(50, "Purchaser key must not exceed 50 characters"),
  portalKeyName: z
    .string()
    .trim()
    .min(1, "Portal key name is required")
    .max(255, "Portal key name must not exceed 255 characters"),
});

const updatePurchaserConfigTemplateSchema = z.object({
  purchaserId: z.number().int().positive(),
  metadata: z.unknown().nullable().optional(),
  searchKeywords: z.string().trim().max(255).nullable().optional(),
  intelligenceFilter: z.unknown().nullable().optional(),
  deductionTypeInvoicePatternForDiscount: z.unknown().nullable().optional(),
  additionalFieldMapping: z.unknown().nullable().optional(),
  discountTermAgreementDaysTolerance: z.number().nullable().optional(),
  isActive: z.boolean().optional(),
});

export type CreatePurchaserDTOShape = z.infer<typeof createPurchaserSchema>;
export type UpdatePurchaserDTOShape = z.infer<typeof updatePurchaserSchema>;
export type CreateBrandPurchaserAssociationDTOShape = z.infer<
  typeof createBrandPurchaserAssociationSchema
>;
export type UpdatePurchaserConfigTemplateDTOShape = z.infer<
  typeof updatePurchaserConfigTemplateSchema
>;
export type CreatePurchaserPortalKeyDTOShape = z.infer<
  typeof createPurchaserPortalKeySchema
>;
export type UpdatePurchaserPortalKeyDTOShape = z.infer<
  typeof updatePurchaserPortalKeySchema
>;

export class CreatePurchaserDTO {
  public readonly data: CreatePurchaserDTOShape;

  constructor(data: CreatePurchaserDTOShape) {
    this.data = data;
  }

  static validate(data: unknown) {
    return createPurchaserSchema.safeParse(data);
  }
}

export class UpdatePurchaserDTO {
  public readonly data: UpdatePurchaserDTOShape;

  constructor(data: UpdatePurchaserDTOShape) {
    this.data = data;
  }

  static validate(data: unknown) {
    return updatePurchaserSchema.safeParse(data);
  }
}

export class CreateBrandPurchaserAssociationDTO {
  public readonly data: CreateBrandPurchaserAssociationDTOShape;
  constructor(data: CreateBrandPurchaserAssociationDTOShape) {
    this.data = data;
  }

  static validate(data: unknown) {
    return createBrandPurchaserAssociationSchema.safeParse(data);
  }
}

export class CreatePurchaserPortalKeyDTO {
  public readonly data: CreatePurchaserPortalKeyDTOShape;

  constructor(data: CreatePurchaserPortalKeyDTOShape) {
    this.data = createPurchaserPortalKeySchema.parse(data);
  }

  static validate(data: unknown) {
    return createPurchaserPortalKeySchema.safeParse(data);
  }
}

export class UpdatePurchaserPortalKeyDTO {
  public readonly data: UpdatePurchaserPortalKeyDTOShape;

  constructor(data: UpdatePurchaserPortalKeyDTOShape) {
    this.data = updatePurchaserPortalKeySchema.parse(data);
  }

  static validate(data: unknown) {
    return updatePurchaserPortalKeySchema.safeParse(data);
  }
}

export class UpdatePurchaserConfigTemplateDTO {
  public readonly data: UpdatePurchaserConfigTemplateDTOShape;

  constructor(data: UpdatePurchaserConfigTemplateDTOShape) {
    this.data = updatePurchaserConfigTemplateSchema.parse(data);
  }

  static validate(data: unknown) {
    return updatePurchaserConfigTemplateSchema.safeParse(data);
  }
}
